import localizationService from './localizationService'
import AuditService from './auditService'
import * as Sentry from "@sentry/react"

// Service for handling subscription API calls
export interface SubscriptionResponse {
  status: 'active' | 'expired' | 'suspended' | 'cancelled' | 'trial'
  expiryDate?: string
  plan: string
  price: number
  nextBilling?: string
  daysUntilNext?: number
  statusMessage?: string
  trialEndDate?: string
  hoursRemaining?: number
}

export interface PaymentResult {
  success: boolean
  data?: {
    orderId: string
    formToken: string
    amount: number
  }
  message?: string
}

export interface TrialSubscriptionResponse {
  success: boolean
  data?: {
    orderId: string
    formToken: string
    publicKey: string
    mode: string
    trialEndDate: string
    subscriptionId: string
    amount: number
  }
  message?: string
}

export interface TrialSubscriptionV2Response {
  success: boolean
  data?: {
    formToken: string
    subscriptionId: string
    aliasOrderId: string
    trialEndDate: string
    publicKey: string
    mode: string
  }
  message?: string
}

export interface SystemPaySubscriptionResponse {
  success: boolean
  data?: {
    subscriptionId: string
    systemPaySubscriptionId: string
    status: string
  }
  message?: string
}

export interface TrialStatusResponse {
  success: boolean
  data?: {
    status: 'trial' | 'active' | 'expired' | 'cancelled'
    trialEndDate?: string
    hoursRemaining?: number
    subscriptionId?: string
  }
  message?: string
}

export class SubscriptionService {
  private baseUrl: string = 'https://payment.nexiamobile.com/api'
  private apiKey: string = 'sk_ciigowl95dv12o3x3v3o6zb'

  private getPlanConfigByCode(planCode: string) {
    if (planCode === 'weekly') {
      return {
        planName: 'Parking Unlimited Weekly',
        frequency: 'weekly',
        amount: 1499
      }
    }
    if (planCode === 'payg') {
      return {
        planName: 'Parking Pay As You Go',
        frequency: 'one-time',
        amount: 190
      }
    }

    return {
      planName: 'Parking Unlimited Monthly',
      frequency: 'monthly',
      amount: 4999
    }
  }

  async checkSubscriptionStatus(email: string): Promise<SubscriptionResponse | null> {
    try {
      console.log('🔍 Vérification du statut d\'abonnement pour:', email)

      const response = await fetch(`${this.baseUrl}/get-subscription-status?customerEmail=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        }
      })

      const result = await response.json()
      console.log('📋 Réponse API statut:', result)

      if (result.success && result.data && result.data.length > 0) {
        // Chercher d'abord un abonnement actif
        const activeSubscription = result.data.find((sub: any) => sub.status === 'active')

        if (activeSubscription) {
          return {
            status: 'active',
            expiryDate: activeSubscription.nextPaymentDate,
            plan: 'monthly',
            price: 49.99,
            nextBilling: activeSubscription.nextPaymentDate,
            daysUntilNext: activeSubscription.daysUntilNextPayment,
            statusMessage: activeSubscription.statusMessage
          }
        }

        // Chercher un abonnement en période d'essai
        const trialSubscription = result.data.find((sub: any) => sub.status === 'trial')

        if (trialSubscription) {
          console.log('📋 Données d\'abonnement reçues:', {
            status: 'trial',
            plan: 'monthly',
            price: 49.99,
            trialEndDate: trialSubscription.nextPaymentDate,
            daysUntilNext: trialSubscription.daysUntilNextPayment
          })

          return {
            status: 'trial',
            expiryDate: trialSubscription.nextPaymentDate,
            plan: 'monthly',
            price: 49.99,
            nextBilling: trialSubscription.nextPaymentDate,
            daysUntilNext: trialSubscription.daysUntilNextPayment,
            statusMessage: `Trial period - ${trialSubscription.daysUntilNextPayment} day(s) remaining`,
            trialEndDate: trialSubscription.nextPaymentDate
          }
        }

        // Chercher d'autres statuts (cancelled, paused, etc.)
        const otherSubscription = result.data[0] // Prendre le premier abonnement trouvé
        if (otherSubscription) {
          return {
            status: otherSubscription.status === 'cancelled' ? 'cancelled' : 'expired',
            expiryDate: otherSubscription.nextPaymentDate,
            plan: 'monthly',
            price: 49.99,
            statusMessage: otherSubscription.statusMessage
          }
        }
      }

      // Pas d'abonnement trouvé
      return {
        status: 'expired',
        plan: 'monthly',
        price: 49.99
      }

    } catch (error) {
      console.error('❌ Erreur lors de la vérification du statut:', error)
      Sentry.captureException(error, {
        tags: {
          service: 'subscriptionService',
          method: 'checkSubscriptionStatus'
        },
        extra: {
          email,
          baseUrl: this.baseUrl
        }
      })
      return null
    }
  }

  async createSubscriptionWithPayment(
    email: string,
    userId?: string,
    options?: {
      planCode?: string
      planName?: string
      subscriptionAmount?: number
      initialAmount?: number
      frequency?: string
    }
  ): Promise<PaymentResult> {
    try {
      console.log('🚀 Création d\'abonnement avec paiement pour:', email)

      // Log payment initiation
      if (userId) {
        await AuditService.logUserActivity({
          userId,
          eventType: 'PAYMENT_INITIATED',
          details: { email, method: 'subscription_with_payment' }
        })
      }

      // Obtenir les données de localisation
      const paymentData = await localizationService.getPaymentData()
      console.log('💰 Données de paiement localisées:', paymentData)

      const planCode = options?.planCode || 'monthly'
      const defaultPlan = this.getPlanConfigByCode(planCode)
      const planName = options?.planName || defaultPlan.planName
      const frequency = options?.frequency || defaultPlan.frequency
      const subscriptionAmount = options?.subscriptionAmount ?? defaultPlan.amount
      const initialAmount = options?.initialAmount ?? subscriptionAmount

      let response;
      let result;

      if (planCode === 'payg') {
        const paygData = {
          amount: initialAmount,
          currency: paymentData.currency,
          orderId: `carstore_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
          customerEmail: email,
          customer: {
            email: email
          }
        };

        response = await fetch('https://adminv2.nexiamobile.com/.netlify/functions/create-payment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.apiKey}`
          },
          body: JSON.stringify(paygData)
        });
        
        result = await response.json();
        console.log('📤 Réponse création paiement Pay As You Go:', result);
      } else {
        const subscriptionData = {
          customerEmail: email,
          planName,
          subscriptionAmount,
          initialAmount,
          frequency,
          currency: paymentData.currency, // Devise localisée
          customerReference: `parking_${email}_${Date.now()}`
        };

        response = await fetch(`${this.baseUrl}/create-subscription-with-payment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.apiKey}`
          },
          body: JSON.stringify(subscriptionData)
        });

        result = await response.json();
        console.log('📤 Réponse création abonnement:', result);
      }

      // Log payment result
      if (userId && result.success && result.data) {
        await AuditService.logPaymentAudit({
          userId,
          paymentId: result.data.orderId,
          amount: paymentData.amount / 100, // Convert cents to currency units
          currency: paymentData.currency,
          paymentStatus: 'initiated'
        })
      }

      return result

    } catch (error) {
      console.error('❌ Erreur lors de la création de l\'abonnement:', error)

      // Log payment error
      if (userId) {
        await AuditService.logUserActivity({
          userId,
          eventType: 'PAYMENT_ERROR',
          details: {
            email,
            error: error instanceof Error ? error.message : 'Unknown error',
            method: 'subscription_with_payment'
          }
        })
      }

      Sentry.captureException(error, {
        tags: {
          service: 'subscriptionService',
          method: 'createSubscriptionWithPayment'
        },
        extra: {
          email,
          userId,
          baseUrl: this.baseUrl
        }
      })

      return {
        success: false,
        message: error instanceof Error ? error.message : 'Erreur inconnue'
      }
    }
  }

  async getSubscriptionId(customerEmail: string): Promise<{ success: boolean; subscriptionId?: string; message?: string }> {
    try {
      console.log('🔍 Récupération de l\'ID d\'abonnement pour:', customerEmail)

      const response = await fetch(`${this.baseUrl}/get-subscription-status?customerEmail=${encodeURIComponent(customerEmail)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        }
      })

      const result = await response.json()
      console.log('📋 Réponse récupération ID:', result)

      if (result.success && result.data && result.data.length > 0) {
        // Chercher d'abord un abonnement actif
        const activeSubscription = result.data.find((sub: any) => sub.status === 'active')
        if (activeSubscription && activeSubscription.id) {
          return {
            success: true,
            subscriptionId: activeSubscription.id
          }
        }

        // Chercher un abonnement en période d'essai
        const trialSubscription = result.data.find((sub: any) => sub.status === 'trial')
        if (trialSubscription && trialSubscription.id) {
          return {
            success: true,
            subscriptionId: trialSubscription.id
          }
        }

        // Prendre le premier abonnement trouvé s'il a un ID
        const firstSubscription = result.data[0]
        if (firstSubscription && firstSubscription.id) {
          return {
            success: true,
            subscriptionId: firstSubscription.id
          }
        }
      }

      return {
        success: false,
        message: 'Aucun abonnement trouvé pour cet email'
      }

    } catch (error) {
      console.error('❌ Erreur lors de la récupération de l\'ID:', error)
      Sentry.captureException(error, {
        tags: {
          service: 'subscriptionService',
          method: 'getSubscriptionId'
        },
        extra: {
          customerEmail,
          baseUrl: this.baseUrl
        }
      })
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Erreur inconnue'
      }
    }
  }

  async cancelSubscription(subscriptionIdOrEmail: string, isEmail: boolean = false, reason: string = 'Annulation demandée par l\'utilisateur'): Promise<{ success: boolean; message?: string }> {
    try {
      let subscriptionId = subscriptionIdOrEmail

      // Si c'est un email, récupérer d'abord l'ID de l'abonnement
      if (isEmail) {
        console.log('🔍 Récupération de l\'ID d\'abonnement pour l\'email:', subscriptionIdOrEmail)
        const idResult = await this.getSubscriptionId(subscriptionIdOrEmail)

        if (!idResult.success || !idResult.subscriptionId) {
          return {
            success: false,
            message: idResult.message || 'Impossible de récupérer l\'ID de l\'abonnement'
          }
        }

        subscriptionId = idResult.subscriptionId
        console.log('✅ ID d\'abonnement récupéré:', subscriptionId)
      }

      console.log('🚫 Annulation de l\'abonnement:', subscriptionId, 'Raison:', reason)

      const response = await fetch(`${this.baseUrl}/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          subscriptionId: subscriptionId,
          reason: reason,
          calculateRefund: false,
          immediateTermination: false
        })
      })

      const result = await response.json()
      console.log('📤 Réponse annulation:', result)

      if (result.success) {
        return {
          success: true,
          message: 'Abonnement annulé avec succès'
        }
      } else {
        return {
          success: false,
          message: result.message || 'Erreur lors de l\'annulation'
        }
      }

    } catch (error) {
      console.error('❌ Erreur lors de l\'annulation:', error)
      Sentry.captureException(error, {
        tags: {
          service: 'subscriptionService',
          method: 'cancelSubscription'
        },
        extra: {
          subscriptionIdOrEmail,
          isEmail,
          baseUrl: this.baseUrl
        }
      })
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Erreur inconnue'
      }
    }
  }

  async getTransactionStatus(orderId: string): Promise<{ success: boolean; data?: any; message?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/get-transaction-status?orderId=${orderId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        }
      })

      const result = await response.json()
      return result

    } catch (error) {
      console.error('❌ Erreur lors de la vérification du statut de transaction:', error)
      Sentry.captureException(error, {
        tags: {
          service: 'subscriptionService',
          method: 'getTransactionStatus'
        },
        extra: {
          orderId,
          baseUrl: this.baseUrl
        }
      })
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Erreur inconnue'
      }
    }
  }

  async createTrialSubscription(email: string, fullName?: string): Promise<TrialSubscriptionResponse> {
    try {
      console.log('🚀 Creating trial subscription for:', email)

      // Obtenir les données de localisation
      const paymentData = await localizationService.getPaymentData()
      console.log('💰 Données de paiement localisées pour V1:', paymentData)

      const requestData = {
        customerEmail: email,
        planName: 'Premium Parking',
        subscriptionAmount: paymentData.amount, // Prix localisé en centimes
        frequency: 'monthly',
        trialDurationHours: 24,
        trialAmount: 100, // Verification charge in cents
        currency: paymentData.currency, // Devise localisée
        customer: {
          name: fullName || 'Premium User',
          phone: '+1 000 000 0000'
        }
      }

      const response = await fetch('https://payment.nexiamobile.com/.netlify/functions/create-subscription-with-trial', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify(requestData)
      })

      const result = await response.json()
      console.log('📤 Trial subscription response:', result)

      return result

    } catch (error) {
      console.error('❌ Error creating trial subscription:', error)
      Sentry.captureException(error, {
        tags: {
          service: 'subscriptionService',
          method: 'createTrialSubscription'
        },
        extra: {
          email,
          fullName,
          baseUrl: this.baseUrl
        }
      })
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  async getTrialStatus(email: string): Promise<TrialStatusResponse> {
    try {
      console.log('🔍 Checking trial status for:', email)

      const response = await fetch(`${this.baseUrl}/get-trial-status?customerEmail=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        }
      })

      const result = await response.json()
      console.log('📋 Trial status response:', result)

      return result

    } catch (error) {
      console.error('❌ Error checking trial status:', error)
      Sentry.captureException(error, {
        tags: {
          service: 'subscriptionService',
          method: 'getTrialStatus'
        },
        extra: {
          email,
          baseUrl: this.baseUrl
        }
      })
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  async cancelTrialSubscription(subscriptionId: string): Promise<{ success: boolean; message?: string }> {
    try {
      console.log('🚫 Cancelling trial subscription:', subscriptionId)

      const response = await fetch(`${this.baseUrl}/cancel-trial-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          subscriptionId: subscriptionId,
          reason: 'User requested cancellation during trial',
          refundVerificationCharge: true
        })
      })

      const result = await response.json()
      console.log('📤 Trial cancellation response:', result)

      return result

    } catch (error) {
      console.error('❌ Error cancelling trial subscription:', error)
      Sentry.captureException(error, {
        tags: {
          service: 'subscriptionService',
          method: 'cancelTrialSubscription'
        },
        extra: {
          subscriptionId,
          baseUrl: this.baseUrl
        }
      })
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  // ========== NOUVELLES MÉTHODES V2 ==========

  async createTrialSubscriptionV2(email: string, fullName?: string): Promise<TrialSubscriptionV2Response> {
    try {
      console.log('🚀 Creating trial subscription V2 for:', email)

      // Obtenir les données de localisation
      const paymentData = await localizationService.getPaymentData()
      console.log('💰 Données de paiement localisées pour V2:', paymentData)

      const requestData = {
        customerEmail: email,
        subscriptionAmount: paymentData.amount, // Prix localisé en centimes
        planName: 'Premium Parking',
        frequency: 'MONTHLY',
        trialDurationDays: 1, // 24 hours = 1 day
        currency: paymentData.currency, // Devise localisée
        customer: {
          name: fullName || 'Premium User'
        }
      }

      const response = await fetch('https://payment.nexiamobile.com/.netlify/functions/create-subscription-with-trial-v2', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey
        },
        body: JSON.stringify(requestData)
      })

      const result = await response.json()
      console.log('📤 Trial subscription V2 response:', result)

      return result

    } catch (error) {
      console.error('❌ Error creating trial subscription V2:', error)
      Sentry.captureException(error, {
        tags: {
          service: 'subscriptionService',
          method: 'createTrialSubscriptionV2'
        },
        extra: {
          email,
          fullName,
          baseUrl: this.baseUrl
        }
      })
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  async createSystemPaySubscription(subscriptionId: string, paymentToken: string): Promise<SystemPaySubscriptionResponse> {
    try {
      console.log('🚀 Creating SystemPay subscription for:', subscriptionId)

      const requestData = {
        subscriptionId: subscriptionId,
        paymentToken: paymentToken
      }

      const response = await fetch('https://payment.nexiamobile.com/.netlify/functions/create-systempay-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey
        },
        body: JSON.stringify(requestData)
      })

      const result = await response.json()
      console.log('📤 SystemPay subscription response:', result)

      return result

    } catch (error) {
      console.error('❌ Error creating SystemPay subscription:', error)
      Sentry.captureException(error, {
        tags: {
          service: 'subscriptionService',
          method: 'createSystemPaySubscription'
        },
        extra: {
          subscriptionId,
          paymentToken,
          baseUrl: this.baseUrl
        }
      })
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  // Méthode pour vérifier le statut de création d'alias - utilise la même logique que V1
  async checkAliasStatus(orderId: string): Promise<{ success: boolean; aliasCreated?: boolean; paymentToken?: string; message?: string }> {
    try {
      console.log('🔍 Checking alias status for order:', orderId)

      // Utiliser la même méthode que V1 qui fonctionne
      const result = await this.getTransactionStatus(orderId)

      if (result.success && result.data) {
        const status = result.data.status

        // Pour V2, on considère que l'alias est créé quand le statut est 'completed'
        // car cela signifie que le formulaire SystemPay a été soumis avec succès
        if (status === 'completed') {
          return {
            success: true,
            aliasCreated: true,
            paymentToken: result.data.paymentToken || result.data.alias || 'alias_created',
            message: 'Alias created successfully'
          }
        } else if (status === 'pending') {
          return {
            success: true,
            aliasCreated: false,
            message: 'Alias creation in progress'
          }
        } else if (status === 'failed') {
          return {
            success: false,
            aliasCreated: false,
            message: 'Alias creation failed'
          }
        }
      }

      return {
        success: true,
        aliasCreated: false,
        message: 'Alias creation in progress'
      }

    } catch (error) {
      console.error('❌ Error checking alias status:', error)
      Sentry.captureException(error, {
        tags: {
          service: 'subscriptionService',
          method: 'checkAliasStatus'
        },
        extra: {
          orderId,
          baseUrl: this.baseUrl
        }
      })
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  // ========== NOUVELLE MÉTHODE VS1 (Paid Trial) ==========

  async createTrialSubscriptionVS1(email: string, userId?: string, fullName?: string): Promise<TrialSubscriptionResponse> {
    try {
      console.log('🚀 Creating trial subscription VS1 for:', email)

      // Obtenir les données de localisation
      const paymentData = await localizationService.getPaymentData()
      console.log('💰 Données de paiement localisées pour VS1:', paymentData)

      const requestData = {
        customerEmail: email,
        planName: 'Premium Parking',
        subscriptionAmount: paymentData.amount, // Prix localisé en centimes
        frequency: 'monthly',
        trialDurationHours: 24,
        trialAmount: 100, // 1.00 unit (e.g. 1€)
        currency: paymentData.currency, // Devise localisée
        customer: {
          name: fullName || 'Premium User',
          phone: '+1 000 000 0000'
        }
      }

      const response = await fetch('https://payment.nexiamobile.com/.netlify/functions/create-subscription-with-trial', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify(requestData)
      })

      const result = await response.json()
      console.log('📤 Trial subscription VS1 response:', result)

      return result

    } catch (error) {
      console.error('❌ Error creating trial subscription VS1:', error)
      Sentry.captureException(error, {
        tags: {
          service: 'subscriptionService',
          method: 'createTrialSubscriptionVS1'
        },
        extra: {
          email,
          userId,
          fullName,
          baseUrl: this.baseUrl
        }
      })
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  async createPaidTrialFromPayg(
    email: string,
    trialAmount: number,
    userId?: string,
    fullName?: string
  ): Promise<TrialSubscriptionResponse> {
    try {
      console.log('🚀 Creating paid trial from PAYG for:', email)

      const paymentData = await localizationService.getPaymentData()
      console.log('💰 Données de paiement localisées pour PAYG trial:', paymentData)

      const requestData = {
        customerEmail: email,
        planName: 'Premium Parking',
        subscriptionAmount: paymentData.amount,
        frequency: 'monthly',
        trialDurationHours: 24,
        trialAmount,
        currency: paymentData.currency,
        customer: {
          name: fullName || 'Premium User',
          phone: '+1 000 000 0000'
        }
      }

      const response = await fetch('https://payment.nexiamobile.com/.netlify/functions/create-subscription-with-trial', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify(requestData)
      })

      const result = await response.json()
      console.log('📤 Paid trial from PAYG response:', result)

      return result

    } catch (error) {
      console.error('❌ Error creating paid trial from PAYG:', error)
      Sentry.captureException(error, {
        tags: {
          service: 'subscriptionService',
          method: 'createPaidTrialFromPayg'
        },
        extra: {
          email,
          trialAmount,
          userId,
          fullName,
          baseUrl: this.baseUrl
        }
      })
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  // ========== NOUVELLE MÉTHODE V3 ==========

  async createTrialSubscriptionV3(email: string, userId?: string, fullName?: string): Promise<TrialSubscriptionResponse> {
    try {
      console.log('🚀 Creating trial subscription V3 for:', email)

      // Obtenir les données de localisation
      const paymentData = await localizationService.getPaymentData()
      console.log('💰 Données de paiement localisées pour V3:', paymentData)

      const requestData = {
        customerEmail: email,
        planName: 'Premium Parking',
        subscriptionAmount: paymentData.amount, // Prix localisé en centimes
        frequency: 'monthly',
        trialDurationHours: 24,
        trialAmount: 0, // FREE - No charges in V3
        currency: paymentData.currency, // Devise localisée
        customer: {
          name: fullName || 'Premium User',
          phone: '+1 000 000 0000'
        }
      }

      const response = await fetch('https://payment.nexiamobile.com/.netlify/functions/create-subscription-with-trial-v3', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify(requestData)
      })

      const result = await response.json()
      console.log('📤 Trial subscription V3 response:', result)

      return result

    } catch (error) {
      console.error('❌ Error creating trial subscription V3:', error)
      Sentry.captureException(error, {
        tags: {
          service: 'subscriptionService',
          method: 'createTrialSubscriptionV3'
        },
        extra: {
          email,
          userId,
          fullName,
          baseUrl: this.baseUrl
        }
      })
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  // Méthode de compatibilité pour l'ancien code
  async redirectToPayment(email: string): Promise<string> {
    try {
      const result = await this.createSubscriptionWithPayment(email)
      if (result.success && result.data) {
        // Retourner une URL factice car le paiement se fait via le formulaire intégré
        return `#payment-${result.data.orderId}`
      }
      throw new Error(result.message || 'Erreur lors de la création du paiement')
    } catch (error) {
      console.error('Error creating payment session:', error)
      Sentry.captureException(error, {
        tags: {
          service: 'subscriptionService',
          method: 'redirectToPayment'
        },
        extra: {
          email,
          baseUrl: this.baseUrl
        }
      })
      throw error
    }
  }
}

export const subscriptionService = new SubscriptionService()
